import { pointInSolid } from '../collision.ts';
import { polygonBounds } from '../geometry.ts';
import { tessellateRing } from '../map/ring.ts';
import { isLegalStartPlacement, startAreaCentroid } from '../map/startArea.ts';
import { toAngle } from '../math.ts';
import { DEFAULT_HULL, type HullDimensions } from '../hullSize.ts';
import { placementClear, type PlacedHull } from '../placement.ts';
import { createRaceState } from '../race.ts';
import type { TurnFeel } from '../turnFeel.ts';
import type { Vec2 } from '../types.ts';
import { courseModel, routeDirection, tailAt, type Course } from './course.ts';
import { botTurnFeel } from './feel.ts';

export type BotStart = {
  position: Vec2;
  heading: number;
};

/** Grid resolution across the start area's bounding box: fine enough to pack the front row. */
const START_SAMPLES = 17;

function startCandidates(course: Course): Vec2[] {
  const map = course.map;
  const centroid = startAreaCentroid(map);

  if (!map.startArea) {
    return pointInSolid(map, centroid) ? [] : [centroid];
  }

  const bounds = polygonBounds(tessellateRing(map.startArea));
  const points: Vec2[] = [centroid];

  if (bounds) {
    for (let row = 0; row < START_SAMPLES; row += 1) {
      for (let col = 0; col < START_SAMPLES; col += 1) {
        points.push({
          x: bounds.minX + ((col + 0.5) / START_SAMPLES) * (bounds.maxX - bounds.minX),
          y: bounds.minY + ((row + 0.5) / START_SAMPLES) * (bounds.maxY - bounds.minY),
        });
      }
    }
  }

  return points.filter((point) => isLegalStartPlacement(map, point));
}

export type ChooseStartOptions = {
  /** Boats already planted; this hull keeps `PLACEMENT_GAP_M` of water to each. */
  placed?: readonly PlacedHull[];
  /** This seat's hull. Defaults to the Skiff. */
  hull?: HullDimensions;
  /** Hull the seat sails; its speed limits shape the route. Defaults to the Skiff. */
  feel?: TurnFeel;
};

/**
 * Where a bot plants and which way it points: the legal start-area spot with the least race
 * left, bow along the quickest route out, clear of every boat already down by the placement
 * gap. That is as far up toward the first gate as the field leaves room for. Null when no spot
 * in the start area is legal and clear.
 */
export function chooseStart(course: Course, options: ChooseStartOptions = {}): BotStart | null {
  const race = createRaceState();
  const model = courseModel(course, options.feel ?? botTurnFeel());
  const placed = options.placed ?? [];
  const hull = options.hull ?? DEFAULT_HULL;
  let best: { start: BotStart; time: number } | null = null;

  for (const position of startCandidates(course)) {
    const tail = tailAt(model, race, position);
    const direction = tail.gate ? routeDirection(model, tail.gate, position) : null;
    const heading = direction ? toAngle(direction) : 0;

    if (!placementClear(placed, position, heading, hull)) {
      continue;
    }

    if (!best || tail.time < best.time) {
      best = { start: { position, heading }, time: tail.time };
    }
  }

  return best?.start ?? null;
}
